/**
 * Product sibling/color grouping utilities.
 * Groups products by base name (before " - ") so color variants link to each other.
 */

export interface SiblingInfo {
  id: string;
  handle: string;
  color: string;
  featuredImageUrl: string | null;
  title: string;
  titleEn?: string | null;
  retailPrice: number;
  retailComparePrice?: number | null;
  /**
   * Optional media payload forwarded from the source product row so that
   * ProductCard can render the sibling's full gallery (swipeable) when a
   * swatch is selected — not just a single featured image. Shapes match
   * `ProductMediaInput` in `productMedia.ts`.
   */
  images?: unknown;
  mediaOverrides?: unknown;
  backupImages?: unknown;
  preOrderEnabled?: boolean;
  totalInventory?: number;
  /** True for the entry that represents the same product the card was rendered for. */
  isCurrent: boolean;
}

interface ProductForSiblings {
  id: string;
  title: string;
  titleEn?: string | null;
  handle: string | null;
  featuredImageUrl: string | null;
  retailPrice?: any;
  retailComparePrice?: any;
  images?: unknown;
  mediaOverrides?: unknown;
  backupImages?: unknown;
  preOrderEnabled?: boolean;
  variants: Array<{ price: any; inventoryQuantity?: number }>;
}

/**
 * Extract the base product name (before the " - " separator).
 * e.g. "Ace - Black" → "Ace"
 */
export function getProductBaseName(title: string): string {
  const dashIdx = title.indexOf(" - ");
  return dashIdx > 0 ? title.substring(0, dashIdx).trim() : title;
}

/**
 * Detect the GARMENT type from a product title (or "" if none). 2026-06-07.
 *
 * Several lines embed the garment in the post-dash segment —
 * "Smash - Black TOP (Áo Đen)", "Ace - Black SKIRT (Váy Đen)" — so the base
 * name collapses to the line ("Smash"/"Ace") and colour swatches mixed tops
 * with skirts (clicking a "colour" switched garment type). productType is
 * unreliable here ("HÀNG LẺ", "PICKLEBALL", "-"), so we detect from the title.
 *
 * Order matters: more-specific pieces (skort/skirt/leggings) are tested before
 * generic "top"/"áo" so a compound doesn't shadow them. Whole-word matching.
 */
const GARMENT_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  // Sculpt line (2026-06-11): leggings come in two CUTS — Standard vs Flare —
  // that are separate products, not colourways. Without distinct garments
  // they'd group together and a "Black" swatch could switch the cut.
  [/\bflare\s+leggings?\b/i, "flare-leggings"],
  [/\bstandard\s+leggings?\b/i, "standard-leggings"],
  // Short-sleeve tops ("Short Sleeve" / "Áo Cộc Tay") must not fall through
  // to the \bshorts?\b pattern (a bottoms garment).
  [/\bshort\s+sleeves?\b|\bcộc tay\b/i, "short-sleeve"],
  [/\bdài tay\b|\blong\s+sleeves?\b/i, "long-sleeve"],
  [/\bskorts?\b/i, "skort"],
  [/\bskirts?\b|\bváy\b/i, "skirt"],
  [/\bleggings?\b|\btights?\b/i, "leggings"],
  [/\bcapris?\b/i, "capri"],
  [/\bshorts?\b/i, "shorts"],
  [/\bpants?\b|\bquần\b/i, "pants"],
  [/\bbra\b/i, "bra"],
  [/\btank\b/i, "tank"],
  [/\bcami\b/i, "cami"],
  [/\bhalter\b/i, "halter"],
  [/\bjacket\b/i, "jacket"],
  [/\bpullover\b/i, "pullover"],
  [/\btee\b/i, "tee"],
  [/\btop\b|\báo\b/i, "top"],
  [/\bset\b|\bbộ\b/i, "set"],
];

export function getProductGarment(title: string): string {
  const t = title || "";
  for (const [re, canon] of GARMENT_PATTERNS) {
    if (re.test(t)) return canon;
  }
  return "";
}

/** Title-case a colour token: "WHITE" → "White", "baby pink" → "Baby Pink". */
function titleCaseColor(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")
    .trim();
}

/**
 * Strip a trailing garment word from a colour label + normalize casing.
 * "Black TOP" → "Black", "Brown Skirt" → "Brown", "WHITE" → "White".
 * Whole-word only so colours like "Sunset" aren't mangled. 2026-06-07 — fixes
 * polluted swatch labels + inconsistent casing ("Black SKIRT" vs "Black Skirt").
 */
export function cleanColorLabel(color: string): string {
  const stripped = (color || "")
    .trim()
    .replace(
      /\s+(?:tops?|skorts?|skirts?|leggings?|tights?|shorts?|pants?|capris?|bra|tank|cami|halter|jacket|pullover|tee|set|áo|váy|quần|bộ)\s*$/iu,
      "",
    )
    .trim();
  return titleCaseColor(stripped) || titleCaseColor(color);
}

/**
 * Extract the color from a product title.
 *
 * Sapo titles follow `<Base> - <Color> (<Vietnamese>)` for the simple
 * case (apparel) — e.g. "Ace - Black (Đen)" → "Black". But many of
 * the accessory product titles imported from the legacy store carry
 * a Vietnamese descriptor mid-title with its own parenthetical:
 *
 *   "Timeless Socks - Tất (Vớ) Cao Cổ EQL - Freesize - White (Trắng)"
 *                       ↑    ↑                            ↑
 *                  first dash + first paren — NOT the colour.
 *
 * The original implementation grabbed everything between the FIRST
 * " - " and the FIRST " (" — which returned "Tất" (just "Sock" in
 * Vietnamese) for every Timeless / Everyday sock. PDPs then rendered
 * a colour swatch labelled "Tất" and sibling lookups grouped all
 * socks under the same fake colour, so customers saw identical
 * swatches with no distinguishing label.
 *
 * Fix: anchor the regex to the END of the string so we always match
 * the FINAL " - <Color> (<VN>)" segment, regardless of how many
 * dashes or parentheticals precede it. Falls back to the last " - "
 * segment when no trailing parenthetical exists, with a placeholder
 * filter to skip non-colour values like "Freesize" / "Default Title".
 */
export function getProductColor(title: string): string {
  // Format 3 (legacy): `<Line> - <Color> (<VN>) - <Piece> (<VN-piece>)`
  //   e.g. "Whisper - White (Trắng) - Top (Áo)"
  //        "Tet Edition - Scarlet (Đỏ cam) - Áo Sport Bra"
  //        "Evolution - White (Trắng) - Áo khoác Jacket"
  // The greedy `(.*)` in pattern 1 was matching the FIRST paren-pair
  // and returning the piece word as the colour → swatch fell back to
  // grey for every product with this two-paren format (~25 products).
  // Strip the trailing piece-and-paren tail FIRST so the underlying
  // colour extraction sees the canonical `<Line> - <Color> (<VN>)`.
  //
  // The piece can be either an English keyword (top/short/skort/etc),
  // a Vietnamese garment word (Áo/Quần/Váy/Set), OR a longer compound
  // like "Áo Sport Bra" / "Set 3 món" / "Áo khoác Jacket". The pattern
  // anchors on the leading garment keyword + accepts any further
  // tokens until the trailing parens.
  // Two variants: piece may or may not be followed by trailing parens.
  //   - "Top (Áo)"           → trailing parens (PIECE_TAIL_RE)
  //   - "Áo Sport Bra"       → no trailing parens (PIECE_TAIL_BARE_RE)
  // Both strip the entire `- <piece>...` chunk so the colour extraction
  // below sees the canonical `<Line> - <Color> (<VN>)` form.
  // Vietnamese sleeve qualifiers ("Dài tay" = long sleeve, "Cộc tay" =
  // short sleeve) are added to the keyword list so titles like
  // "Empower - Dark Grey (Xám đen) - Dài tay" correctly extract
  // "Dark Grey" instead of returning "Dài tay" as the colour.
  const PIECE_TAIL_RE =
    /\s+-\s+(?:[Áá]o|[Qq]uần|[Vv]áy|[Ss]et|[Dd]ây|[Tt]úi|[Mm]út|[Tt]op|[Ss]hort|[Ss]horts|[Ss]kort|[Ss]korts|[Ss]kirt|[Tt]ank|[Cc]ami|[Hh]alter|[Bb]ra|[Jj]acket|[Pp]ant|[Pp]ants|[Ll]egging|[Ll]eggings|[Tt]ights|[Cc]apri|[Cc]over|[Tt]ee|[Pp]ullover|[Ll]ong [Oo]nly|[Ss]hort [Oo]nly|[Pp]iece|[Bb]ottom|[Dd]ài tay|[Cc]ộc tay|[Ff]lare|[Ss]tandard)\b[^()]*\s+\([^()]+\)\s*$/u;
  const PIECE_TAIL_BARE_RE =
    /\s+-\s+(?:[Áá]o|[Qq]uần|[Vv]áy|[Ss]et|[Dd]ây|[Tt]úi|[Mm]út|[Tt]op|[Ss]hort|[Ss]horts|[Ss]kort|[Ss]korts|[Ss]kirt|[Tt]ank|[Cc]ami|[Hh]alter|[Bb]ra|[Jj]acket|[Pp]ant|[Pp]ants|[Ll]egging|[Ll]eggings|[Tt]ights|[Cc]apri|[Cc]over|[Tt]ee|[Pp]ullover|[Ll]ong [Oo]nly|[Ss]hort [Oo]nly|[Pp]iece|[Bb]ottom|[Dd]ài tay|[Cc]ộc tay|[Ff]lare|[Ss]tandard)\b[^()]*\s*$/u;
  let working = title
    // Strip a trailing standalone garment word that follows the colour
    // parenthetical — "Slam - Black (Đen) Set" → "Slam - Black (Đen)" — so
    // the canonical extraction below sees the colour. Without this, the
    // anchored Format-2 regex (must end in ")") failed and Slam sets returned
    // no colour at all. 2026-06-07.
    .replace(
      /\)\s+(?:set|top|skorts?|skirts?|bra|tank|cami|halter|jacket|pants?|leggings?|tights?|shorts?|capri|pullover|tee|bộ|áo|váy|quần)\s*$/iu,
      ")",
    )
    .replace(PIECE_TAIL_RE, "")
    .replace(PIECE_TAIL_BARE_RE, "");

  // Format 2: `<Line> - <Color> (<VN-color>)$` — the canonical form.
  const m = working.match(/^(.*) - ([^()]*?) \(([^()]+)\)\s*$/u);
  if (m) return cleanColorLabel(m[2]);

  // Format 1: `<Line> - <Color>` (no parenthetical) — take the
  // LAST segment after " - ". Skip if it contains parens or is a known
  // placeholder (Default Title / Freesize / One Size) so accessories
  // without a colour don't render a junk swatch labelled "Freesize".
  // Also skip if it's a single-letter size (e.g. "Attitude - Tím - M"
  // would otherwise return "M" instead of the colour).
  const lastDash = working.lastIndexOf(" - ");
  if (lastDash < 0) return "";
  const afterDash = working.substring(lastDash + 3).trim();
  if (/[()]/.test(afterDash)) return "";
  if (/^(default title|freesize|free size|one size|onesize|os|n\/a)$/i.test(afterDash)) return "";
  if (/^(xxs|xs|s|m|l|x|xl|xxl|2xl|3xl)$/i.test(afterDash)) {
    // Size suffix — peel and try one more segment back.
    const inner = working.substring(0, lastDash);
    const innerDash = inner.lastIndexOf(" - ");
    if (innerDash < 0) return "";
    const inner2 = inner.substring(innerDash + 3).trim();
    if (/[()]/.test(inner2)) return "";
    return cleanColorLabel(inner2);
  }
  return cleanColorLabel(afterDash);
}

/**
 * Build a Map of productId → all colour variants of the same base product
 * (including the current product itself, marked with `isCurrent: true`).
 *
 * The current product is included so the product card can render its own
 * colour swatch in the row. Without it, a buyer looking at "Smash – Pink"
 * would see Black/Brown/Moss dots but no Pink dot, which is confusing.
 */
export function buildSiblingsMap(
  products: ProductForSiblings[]
): Map<string, SiblingInfo[]> {
  type GroupMember = Omit<SiblingInfo, "isCurrent">;
  const groups = new Map<string, GroupMember[]>();

  for (const p of products) {
    const base = getProductBaseName(p.title);
    // Group key includes the GARMENT so colour swatches stay same-garment:
    // a Smash Top and a Smash Skirt share the base "Smash" but are different
    // products and must NOT appear as each other's colour options. 2026-06-07.
    const garment = getProductGarment(p.title);
    const groupKey = `${base}|${garment}`;
    const color = getProductColor(p.title);
    if (!color || !p.handle) continue;

    const retailPrice = p.retailPrice ? Number(p.retailPrice) : Number(p.variants[0]?.price || 0);
    const retailComparePrice =
      p.retailComparePrice != null ? Number(p.retailComparePrice) : null;
    const totalInventory = p.variants.reduce(
      (sum, v) => sum + (typeof v.inventoryQuantity === "number" ? v.inventoryQuantity : 0),
      0,
    );

    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey)!.push({
      id: p.id,
      handle: p.handle,
      color,
      featuredImageUrl: p.featuredImageUrl,
      title: p.title,
      titleEn: p.titleEn ?? null,
      retailPrice,
      retailComparePrice,
      images: p.images,
      mediaOverrides: p.mediaOverrides,
      backupImages: p.backupImages,
      preOrderEnabled: p.preOrderEnabled,
      totalInventory,
    });
  }

  const result = new Map<string, SiblingInfo[]>();

  for (const [, members] of groups) {
    // A single colour with no other variants is not a "sibling" group.
    if (members.length <= 1) continue;
    for (const m of members) {
      result.set(
        m.id,
        members.map((s) => ({ ...s, isCurrent: s.id === m.id })),
      );
    }
  }

  return result;
}

/**
 * One tile per style (website audit, 13 Oct 2026, item 9).
 *
 * The catalogue stores one Product per colourway, so a listing of 160
 * products is really ~60 styles and the grid showed "Ace - Black", "Ace -
 * Navy", "Ace - Pink" as three tiles in a row. Aritzia (and every multi-brand
 * grid) shows one tile per style with colour dots. This keeps the FIRST
 * colourway of each style in the incoming sort order, preferring one that is
 * in stock (or pre-orderable) so the tile is buyable; the other colourways
 * stay reachable through the swatches the card already renders from its
 * `siblings`. Styles are keyed the same way as `buildSiblingsMap` (base name
 * + garment) so a Smash Top never swallows a Smash Skirt. Products with no
 * recognisable colour are never merged.
 */
/**
 * The STYLE a colourway belongs to ("Ace - Black (Đen) SKIRT" → "Ace|skirt"),
 * or null when the title carries no colour (not a colourway of anything).
 */
export function colourwayStyleKey(title: string): string | null {
  if (!getProductColor(title)) return null;
  return `${getProductBaseName(title)}|${getProductGarment(title)}`;
}

export function collapseColourways<
  T extends { title: string; preOrderEnabled?: boolean | null; variants?: Array<{ inventoryQuantity?: number | null }> | null },
>(products: T[]): T[] {
  const keyOf = (p: T) => colourwayStyleKey(p.title);
  const buyable = (p: T) =>
    !!p.preOrderEnabled ||
    (p.variants || []).some((v) => (typeof v.inventoryQuantity === "number" ? v.inventoryQuantity : 0) > 0);

  // First pass: position of each style's first member and its best candidate.
  const slot = new Map<string, number>();
  const pick = new Map<string, T>();
  const out: Array<T | null> = [];
  for (const p of products) {
    const key = keyOf(p);
    if (!key) {
      out.push(p);
      continue;
    }
    if (!slot.has(key)) {
      slot.set(key, out.length);
      pick.set(key, p);
      out.push(null); // placeholder, filled below
      continue;
    }
    const current = pick.get(key)!;
    if (!buyable(current) && buyable(p)) pick.set(key, p);
  }
  for (const [key, idx] of slot) out[idx] = pick.get(key)!;
  return out.filter((p): p is T => p !== null);
}
